import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Note, User } from '../../types/api';
import apiService from '../../services/api';
import { formatRelativeDateTime } from '../../utils/dateUtils';

interface PatientNotesListProps {
    patientId: number;
    users: User[];
    currentUserId?: number;
    hasSuicidalQuestionnaire?: boolean;
}

const PaperPlaneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

export const PatientNotesList: React.FC<PatientNotesListProps> = ({
    patientId,
    users,
    currentUserId,
    hasSuicidalQuestionnaire = false,
}) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newNoteText, setNewNoteText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [hoveredNoteId, setHoveredNoteId] = useState<number | null>(null);

    const token = localStorage.getItem('token') || '';
    const loggedInUserId = currentUserId || parseInt(localStorage.getItem('user_id') || '0', 10);
    const isFetchingRef = useRef(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [bubbleMaxHeight, setBubbleMaxHeight] = useState<string>('200px');

    const userNameMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [users]);

    const getUserName = (userId: number): string => {
        if (loggedInUserId > 0 && userId === loggedInUserId) return 'You';
        return userNameMap[userId] || 'Unknown User';
    };

    const isHebrew = (text: string) => /[\u0590-\u05FF]/.test(text);

    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    const fetchNotes = React.useCallback(async () => {
        if (!token || !patientId) return;
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setIsLoading(true);
        setError(null);
        try {
            const notesData = await apiService.getNotes({
                token,
                object: 'patient',
                object_id: patientId,
            });
            setNotes([...notesData].sort((a, b) => a.created_at - b.created_at));
        } catch (err: any) {
            setError('Failed to load notes');
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [token, patientId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    useEffect(() => {
        if (!isLoading) scrollToBottom();
    }, [notes, isLoading]);

    useLayoutEffect(() => {
        if (hasSuicidalQuestionnaire) return;

        const measure = () => {
            if (!containerRef.current) return;

            // Find the nearest scrollable ancestor (the tab content div)
            let scrollEl: HTMLElement | null = containerRef.current.parentElement;
            while (scrollEl) {
                const overflow = window.getComputedStyle(scrollEl).overflowY;
                if (overflow === 'auto' || overflow === 'scroll') break;
                scrollEl = scrollEl.parentElement;
            }
            if (!scrollEl) return;

            const containerTop = containerRef.current.getBoundingClientRect().top;
            const scrollRect = scrollEl.getBoundingClientRect();
            const reserved = 160;
            const initial = Math.max(80, Math.floor(scrollEl.clientHeight - (containerTop - scrollRect.top) - reserved));
            setBubbleMaxHeight(`${initial}px`);

            // After setting the height, check if the scroll container still overflows
            // and trim further if needed
            requestAnimationFrame(() => {
                if (!scrollEl) return;
                const overflow = scrollEl.scrollHeight - scrollEl.clientHeight;
                if (overflow > 0) {
                    setBubbleMaxHeight(prev => `${Math.max(80, parseInt(prev) - overflow - 2)}px`);
                }
            });
        };

        measure();

        // Observe the about-tab ancestor (3 levels up) so sibling sections resizing triggers re-measure
        const aboutTabEl = containerRef.current?.parentElement?.parentElement?.parentElement;
        const ro = new ResizeObserver(measure);
        if (aboutTabEl) ro.observe(aboutTabEl);
        window.addEventListener('resize', measure);

        // Re-measure after async patient data typically finishes loading
        const t1 = setTimeout(measure, 200);
        const t2 = setTimeout(measure, 800);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [hasSuicidalQuestionnaire]);


    const handleAddNote = async () => {
        if (!newNoteText.trim()) return;
        setIsSaving(true);
        try {
            await apiService.createNote({
                token,
                object: 'patient',
                object_id: patientId,
                text: newNoteText.trim(),
            });
            setNewNoteText('');
            await fetchNotes();
        } catch (err: any) {
            setError('Failed to create note');
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddNote();
        }
    };

    const handleSaveEdit = async (noteId: number) => {
        if (!editingText.trim()) return;
        setIsSaving(true);
        try {
            await apiService.updateNote({
                token,
                id: noteId,
                object: 'patient',
                object_id: patientId,
                text: editingText.trim(),
            });
            setEditingNoteId(null);
            setEditingText('');
            await fetchNotes();
        } catch (err: any) {
            setError('Failed to update note');
        } finally {
            setIsSaving(false);
        }
    };

    const notesMaxHeight = hasSuicidalQuestionnaire ? '160px' : bubbleMaxHeight;

    const renderNote = (note: Note) => {
        const isOwn = loggedInUserId > 0 && note.created_by === loggedInUserId;
        const isRTL = isHebrew(note.text);
        const isEditing = editingNoteId === note.id;
        const isHovered = hoveredNoteId === note.id;

        return (
            <div
                key={note.id}
                className="w-full flex flex-col"
                onMouseEnter={() => setHoveredNoteId(note.id)}
                onMouseLeave={() => setHoveredNoteId(null)}
            >
                <div className={`flex items-center gap-1.5 mb-1 text-xs text-gray-500 ${isOwn ? 'flex-row-reverse' : ''}`}>
                    <span className="font-semibold text-gray-600">{getUserName(note.created_by)}</span>
                    <span>{formatRelativeDateTime(note.created_at)}</span>
                </div>

                {isEditing ? (
                    <div className="flex gap-2 w-full max-w-[90%]">
                        <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white"
                            style={{ minHeight: '60px', direction: isRTL ? 'rtl' : 'ltr' }}
                            autoFocus
                            disabled={isSaving}
                        />
                        <div className="flex flex-col gap-1">
                            <button
                                onClick={() => handleSaveEdit(note.id)}
                                disabled={isSaving || !editingText.trim()}
                                className="px-2 py-1 text-xs bg-[#366A75] text-white rounded-lg hover:bg-[#2d5a63] disabled:opacity-50 disabled:cursor-not-allowed"
                            >✓</button>
                            <button
                                onClick={() => { setEditingNoteId(null); setEditingText(''); }}
                                disabled={isSaving}
                                className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
                            >✕</button>
                        </div>
                    </div>
                ) : (
                    <div className={`flex items-start gap-1 w-full ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        {isOwn && (
                            <button
                                onClick={() => { setEditingNoteId(note.id); setEditingText(note.text); }}
                                className={`text-gray-400 hover:text-gray-600 text-sm p-0.5 flex-shrink-0 mt-1 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                title="Edit note"
                            >✎</button>
                        )}
                        <div
                            className={`max-w-[85%] px-3 py-2 text-sm leading-normal rounded-2xl ${
                                isOwn
                                    ? 'bg-gray-100 text-gray-800 rounded-tr-sm'
                                    : 'bg-gray-200 text-gray-800 rounded-tl-sm'
                            }`}
                            style={isRTL ? { direction: 'rtl' } : undefined}
                        >
                            {note.text}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div ref={containerRef} className="flex flex-col gap-2 min-w-0 overflow-hidden">
            {/* Scrollable notes area — capped so outer panel never needs to scroll */}
            <div
                ref={scrollRef}
                className="flex flex-col gap-3 overflow-y-auto overflow-x-hidden pr-2"
                style={{ maxHeight: notesMaxHeight }}
            >
                {isLoading ? (
                    <div className="text-sm text-gray-500 italic text-center py-2">Loading...</div>
                ) : error ? (
                    <div className="text-sm text-red-600 text-center">{error}</div>
                ) : notes.length === 0 ? (
                    <div className="text-sm text-gray-500 italic text-center py-2">No notes yet</div>
                ) : (
                    notes.map(renderNote)
                )}
            </div>

            {/* Input */}
            <div className="flex items-center gap-2 mt-1 min-w-0">
                <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a note..."
                    className="flex-1 min-w-0 px-4 py-2.5 text-sm bg-gray-100 border-0 rounded-full focus:outline-none resize-none"
                    style={{ minHeight: '40px', maxHeight: '100px' }}
                    disabled={isSaving}
                    rows={1}
                />
                <button
                    onClick={handleAddNote}
                    disabled={isSaving || !newNoteText.trim()}
                    title="Add note"
                    aria-label="Add note"
                    data-ai-id="add-patient-note"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#9AB5B2] text-white hover:bg-[#7a9e9b] disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                >
                    <PaperPlaneIcon />
                </button>
            </div>
        </div>
    );
};
